import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface ExploreItem {
  id: number;
  media_type: 'movie' | 'tv' | 'person';
  title?: string;
  name?: string;
  original_title?: string;
  original_name?: string;
  poster_path?: string | null;
  profile_path?: string | null;
  backdrop_path?: string | null;
  vote_average?: number;
  release_date?: string;
  first_air_date?: string;
  overview?: string;
  known_for_department?: string;
  popularity?: number;
}

export interface ExploreResponse {
  page: number;
  results: ExploreItem[];
  total_pages: number;
  total_results: number;
}

@Injectable({
  providedIn: 'root'
})
export class ExploreService {
  private readonly baseUrl = environment.backendUrl;

  constructor(private http: HttpClient) {}

  getMovies(category: string, page: number = 1, language: string = 'en-US'): Observable<ExploreResponse> {
    let endpoint = 'popular';
    if (category === 'now-playing' || category === 'now_playing') {
      endpoint = 'now_playing';
    } else if (category === 'top-rated' || category === 'top_rated') {
      endpoint = 'top_rated';
    } else if (category === 'upcoming') {
      endpoint = 'upcoming';
    }

    const params = new HttpParams()
      .set('page', page.toString())
      .set('language', language);

    return this.http.get<ExploreResponse>(`${this.baseUrl}/api/movies/${endpoint}`, { params });
  }

  getTvShows(category: string, page: number = 1, language: string = 'en-US'): Observable<ExploreResponse> {
    let endpoint = 'popular';
    if (category === 'airing-today' || category === 'airing_today') {
      endpoint = 'airing_today';
    } else if (category === 'on-the-air' || category === 'on_the_air') {
      endpoint = 'on_the_air';
    } else if (category === 'top-rated' || category === 'top_rated') {
      endpoint = 'top_rated';
    }

    const params = new HttpParams()
      .set('page', page.toString())
      .set('language', language);

    return this.http.get<ExploreResponse>(`${this.baseUrl}/api/tv/${endpoint}`, { params });
  }

  getPeople(category: string = 'popular', page: number = 1, language: string = 'en-US'): Observable<ExploreResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('language', language);
    return this.http.get<ExploreResponse>(`${this.baseUrl}/api/popular/actor`, { params });
  }

  searchMovies(query: string, page: number = 1, language: string = 'en-US'): Observable<ExploreResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('language', language);
    return this.http.get<ExploreResponse>(
      `${this.baseUrl}/api/search/movie/${encodeURIComponent(query.trim())}`,
      { params }
    );
  }

  searchTvShows(query: string, page: number = 1, language: string = 'en-US'): Observable<ExploreResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('language', language);
    return this.http.get<ExploreResponse>(
      `${this.baseUrl}/api/search/tv/${encodeURIComponent(query.trim())}`,
      { params }
    );
  }

  searchPeople(query: string, page: number = 1, language: string = 'en-US'): Observable<ExploreResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('language', language);
    return this.http.get<ExploreResponse>(
      `${this.baseUrl}/api/search/actor/${encodeURIComponent(query.trim())}`,
      { params }
    );
  }
}
